'use client';

import { useState, useMemo } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useFormatter, useTranslations } from 'next-intl';
import { ChevronRight, Globe, Loader2, Map, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatPublicCurrency } from '@/lib/format/currency-display';
import { Flag } from '@/components/ui/flag/flag';
import { Input } from '@/components/ui/input/input';
import { trpc } from '@/lib/trpc';
import { useRegional } from '@/components/providers/regional-provider';

interface Country {
  id: string;
  name: string;
  slug: string;
  code: string;
  ridesCount: number;
  entrantsCount: number;
  totalRaised: number;
}

interface CountryEventStat {
  countryCode: string;
  countryName: string;
  ridesCount: number;
  entrantsCount: number;
  totalRaised: number;
  donationCount: number;
  currencyCode: string;
}

function toSlug(value: string) {
  return value
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

interface EventsPageProps {
  prefetchedData?: { countryEventStats: CountryEventStat[]; currencyCode: string };
}

export function EventsPage({ prefetchedData }: EventsPageProps) {
  const t = useTranslations('events');
  const format = useFormatter();
  const { currency: regionalCurrency } = useRegional();
  const [searchQuery, setSearchQuery] = useState('');
  const { data, isLoading } = trpc.publicLookups.countryEventStats.useQuery(
    { currencyCode: regionalCurrency },
    {
      ...(prefetchedData?.countryEventStats &&
        prefetchedData.currencyCode === regionalCurrency && {
          initialData: prefetchedData.countryEventStats,
        }),
    },
  );

  const countriesData = useMemo<Country[]>(
    () =>
      (data ?? []).map((country: CountryEventStat) => ({
        id: country.countryCode,
        name: country.countryName,
        slug: toSlug(country.countryName),
        code: country.countryCode,
        ridesCount: country.ridesCount,
        entrantsCount: country.entrantsCount,
        totalRaised: country.totalRaised,
      })),
    [data],
  );

  const globalStats = useMemo(
    () => ({
      totalCountries: countriesData.length,
      totalRides: countriesData.reduce((sum, country) => sum + country.ridesCount, 0),
      totalEntrants: countriesData.reduce((sum, country) => sum + country.entrantsCount, 0),
      totalRaised: countriesData.reduce((sum, country) => sum + country.totalRaised, 0),
    }),
    [countriesData],
  );

  const filteredCountries = useMemo(() => {
    if (!searchQuery) {
      return [...countriesData].sort((a, b) => a.name.localeCompare(b.name));
    }

    const lowerQuery = searchQuery.toLowerCase();
    return countriesData
      .filter(
        (country) =>
          country.name.toLowerCase().includes(lowerQuery) ||
          country.code.toLowerCase().includes(lowerQuery),
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [countriesData, searchQuery]);

  return (
    <>
      <main className="flex-1">
        {/* Hero / Cover Section */}
        <section className="relative h-[280px] md:h-[460px]">
          <Image
            src="/images/dgr/HeroImage.jpg"
            alt="Events"
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />

          {/* Hero Content */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <h1 className="text-4xl md:text-5xl font-bold text-white">
                  {t('eventsWorldwide')}
                </h1>
              </div>
              <p className="text-white/80 text-lg max-w-xl mx-auto px-4">
                {t('globalSubtitle', { count: globalStats.totalCountries })}
              </p>
            </div>
          </div>
        </section>

        {/* Global Stats */}
        <section className="border-b border-stone-200 bg-stone-50">
          <div className="container max-w-6xl py-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6">
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-dgr-gold-hover">
                  {formatPublicCurrency(format, globalStats.totalRaised, regionalCurrency)}
                </p>
                <p className="text-stone-500 text-sm">{t('raised')}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-stone-800">
                  {globalStats.totalRides}
                </p>
                <p className="text-stone-500 text-sm">{t('rides')}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-stone-800">
                  {format.number(globalStats.totalEntrants)}
                </p>
                <p className="text-stone-500 text-sm">{t('participants')}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-stone-800">
                  {globalStats.totalCountries}
                </p>
                <p className="text-stone-500 text-sm">{t('countries')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Countries Content */}
        <section className="py-8 bg-dgr-cream">
          <div className="container max-w-6xl">
            {/* Search + Global Map */}
            <div className="flex items-center gap-3 mb-8">
              <div className="relative w-full md:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  placeholder={t('searchCountries')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-10 pl-10 bg-white border-stone-300 text-stone-900 placeholder:text-stone-400"
                />
              </div>
              <Button asChild variant="outline" className="h-10 gap-2 shrink-0">
                <Link href="/map">
                  <Map className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('viewGlobalMap')}</span>
                  <span className="sm:hidden">{t('map')}</span>
                </Link>
              </Button>
            </div>

            {isLoading ? (
              <div className="py-16 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-dgr-gold animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredCountries.map((country) => (
                  <Link
                    key={country.id}
                    href={`/events/${country.slug}`}
                    className="group bg-white rounded-xl border border-stone-200 p-5 hover:border-dgr-gold hover:shadow-md transition-all"
                  >
                    <div className="flex items-start gap-4">
                      <Flag code={country.code} size="lg" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-semibold text-stone-900 group-hover:text-dgr-gold-hover transition-colors">
                            {country.name}
                          </h3>
                          <ChevronRight className="w-5 h-5 text-stone-400 group-hover:text-dgr-gold-hover transition-colors" />
                        </div>
                        <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                          <div>
                            <p className="text-stone-500">{t('rides')}</p>
                            <p className="font-semibold text-stone-800">{country.ridesCount}</p>
                          </div>
                          <div>
                            <p className="text-stone-500">{t('riders')}</p>
                            <p className="font-semibold text-stone-800">
                              {format.number(country.entrantsCount)}
                            </p>
                          </div>
                          <div>
                            <p className="text-stone-500">{t('raised')}</p>
                            <p className="font-semibold text-dgr-gold-hover">
                              {formatPublicCurrency(format, country.totalRaised, regionalCurrency)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* Empty State */}
            {!isLoading && filteredCountries.length === 0 && (
              <div className="py-16 text-center">
                <Globe className="w-16 h-16 text-stone-300 mx-auto mb-6" />
                <h2 className="text-2xl font-semibold text-stone-900 mb-2">
                  {t('noCountriesFound')}
                </h2>
                <p className="text-stone-500 max-w-md mx-auto">{t('noCountriesDescription')}</p>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}
